import { Funnel, FunnelX } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import InfiniteScroll from "react-infinite-scroll-component";
import { HeadersFunction, LoaderFunction, MetaFunction, useLoaderData, useRouteError, useSearchParams } from "react-router";
import ArticleComponent from "~/components/articleCards/ArticleComponent";
import SelectArticleType from "~/components/articleCards/SelectArticleType";
import BlogFilterMobile from "~/components/blog/BlogFilterMobile";
import BreadCrubComponent from "~/components/shared-components/BreadCrubComponent";
import ClientOnly from "~/components/shared-components/ClentOnly";
import LoadingSpinner from "~/components/shared-components/LoadingSpinner";
import NodataFound from "~/components/shared-components/NoDataFound";
import NoHasMoreText from "~/components/shared-components/NoHasMoreText";
import { Sheet, SheetContent, SheetTrigger } from "~/components/ui/sheet";
import { useIsMobile } from "~/hook/use-mobile";
import useDisplayStickyButton from "~/hook/useDisplayStickyButton";
import { ArticleType, categoryAndAutherProps } from "~/interface/article/article";
import { getPageMeta } from "~/lib/helperFuncation/metaHelper";
import { CACHED_PAGE, FALLBACK_IMAGE_URL, LANG } from "~/utils/constant";
import { computeEtagFromData, homeRouteString } from "~/utils/helper-funcations";
import { metaService } from "~/utils/metaService";
import { ApiService } from "~/utils/service";

export interface BlogProps {
  blog: ArticleType[];
  title: string;
  blogCategory: categoryAndAutherProps[];
  author: categoryAndAutherProps[];
  selectedCategoryId: number | null;
  selectedAuthorId: number | null;
  lang: string;
  totalBlogCount: number;
}

export const meta: MetaFunction = ({ location, params }) => {
  const pathname = location?.pathname || "/";
  const lang = params.lang || "en";
  const meta = getPageMeta("blog", lang);

  return metaService({
    title: meta.metaTitle,
    description: meta.metaDescription,
    ogTitle: meta.ogTitle,
    ogDescription: meta.ogDescription,
    ogUrl: pathname,
    ogImg: meta.ogImage || FALLBACK_IMAGE_URL,
    ogWidth: meta.ogImageWidth || "1200",
    ogHeight: meta.ogImageHeight || "630",
    canonicalLink: pathname,
    hreflangs: [
      { hrefLang: "x-default", href: `${pathname}` },
      { hrefLang: "en", href: `${pathname}` },
      { hrefLang: "gu", href: `${pathname}` },
    ],
  });
};

export const loader: LoaderFunction = async ({ params, request }) => {
  const lang = params.lang;

  //get search params from url
  const searchParams = new URL(request.url).searchParams;
  const selectedCategory = searchParams.get("category");
  const selectedAuthor = searchParams.get("author");

  //fetch author name array from master data and blog category
  const apiService = new ApiService(lang || LANG.english);
  const authorResponse = await apiService.getMasterData();
  const author = authorResponse["blog_author"] || [];
  const blogCategory = authorResponse["blog_category"] || [];

  //find category and auther from author and blog category
  const selectedCategoryId =
    blogCategory.length > 0 &&
    selectedCategory &&
    blogCategory?.find((cat: categoryAndAutherProps) => cat?.text?.toLowerCase() === selectedCategory?.toLowerCase())?.id;
  const selectedAuthorId =
    author.length > 0 &&
    selectedAuthor &&
    author?.find((aut: categoryAndAutherProps) => aut?.text?.toLowerCase() === selectedAuthor?.toLowerCase())?.id;

  //fetch blog data
  const responce = await apiService.getBlogData({
    pageIndex: 1,
    pageSize: 12,
    category: selectedCategoryId ?? null,
    author: selectedAuthorId ?? null,
  });
  const blog = responce?.rows || [];
  const totalBlogCount = responce?.count || 0;
  const title = lang === LANG.english ? "Blogs" : "બ્લોગ્સ";

  const DATA = {
    blog,
    title,
    author,
    blogCategory,
    selectedCategoryId,
    selectedAuthorId,
    lang,
    totalBlogCount,
  };

  const etag = computeEtagFromData(DATA);
  const ifNoneMatch = request.headers.get("If-None-Match");

  if (ifNoneMatch === etag) {
    return new Response(null, {
      status: 304,
      headers: {
        ETag: etag,
      },
    });
  }

  return Response.json(DATA, {
    headers: { ...CACHED_PAGE.BLOG.HEADERS, ETag: etag },
  });
};

export const headers: HeadersFunction = ({ loaderHeaders }) => loaderHeaders;

export const handle = {
  breadcrumb: (match: any) => {
    const lang = match.params.lang;
    return (
      <BreadCrubComponent
        breadCrub={[
          {
            path: homeRouteString(lang).path,
            title: homeRouteString(lang).home,
          },
        ]}
      />
    );
  },
};

function Blog() {
  const { blog, author, blogCategory, selectedCategoryId, selectedAuthorId, lang, totalBlogCount } = useLoaderData<BlogProps>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  // Memoize to prevent unnecessary re-renders from scroll events
  const { showTopButton } = useDisplayStickyButton();

  const [blogData, setBlogData] = useState<ArticleType[]>(blog);
  const [loading, setLoading] = useState(false);
  const [pageIndex, setPageIndex] = useState(1);
  const [hasMore, setHasMore] = useState(blog.length >= 12);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isPrefetching, setIsPrefetching] = useState(false);
  const [prefetchedData, setPrefetchedData] = useState<ArticleType[] | null>(null);

  // Track if any filters are applied
  const hasFiltersApplied = selectedCategoryId !== null || selectedAuthorId !== null;

  // Memoize apiService to prevent recreation on every render
  const apiService = useMemo(() => new ApiService(lang || LANG.english), [lang]);

  // Prefetch data function
  const prefetchNextPage = useCallback(
    async (currentPageIndex: number) => {
      if (isPrefetching || !hasMore) return;

      setIsPrefetching(true);
      try {
        const response = await apiService.getBlogData({
          pageIndex: currentPageIndex + 1,
          pageSize: 12,
          category: selectedCategoryId ?? null,
          author: selectedAuthorId ?? null,
        });
        setPrefetchedData(response.rows || []);
        setIsPrefetching(false);
      } catch (error) {
        console.error("Error prefetching next page:", error);
        setIsPrefetching(false);
      }
    },
    [isPrefetching, hasMore, selectedCategoryId, selectedAuthorId, apiService],
  );

  const handleFetchNextPageBlogData = useCallback(async () => {
    if (loading || !hasMore) return;

    // Use prefetched data if available
    if (prefetchedData && prefetchedData.length > 0) {
      setBlogData((prevData) => [...prevData, ...prefetchedData]);
      setPageIndex((prevIndex) => prevIndex + 1);
      setPrefetchedData(null);
      setHasMore(prefetchedData.length >= 12);

      // Prefetch next page with current page index
      prefetchNextPage(pageIndex + 1);
    } else {
      // Fallback to direct fetch if prefetch not ready
      setLoading(true);
      try {
        const response = await apiService.getBlogData({
          pageIndex: pageIndex + 1,
          pageSize: 12,
          category: selectedCategoryId ?? null,
          author: selectedAuthorId ?? null,
        });
        const newBlogData = response.rows;
        setBlogData((prevData) => [...prevData, ...newBlogData]);
        setPageIndex((prevIndex) => prevIndex + 1);
        setLoading(false);
        setHasMore(newBlogData.length >= 12);

        // Prefetch next page with current page index
        prefetchNextPage(pageIndex + 1);
      } catch (error) {
        console.error("Error fetching next page blog data:", error);
        setLoading(false);
        setHasMore(false);
      }
    }
  }, [selectedCategoryId, selectedAuthorId, apiService, prefetchNextPage, pageIndex, loading, prefetchedData, hasMore]);

  // Reset on filter/category change
  useEffect(() => {
    setBlogData(blog);
    setPageIndex(1);
    setHasMore(blog.length >= 12);
    setPrefetchedData(null);
    setIsPrefetching(false);
  }, [selectedCategoryId, selectedAuthorId, blog]);

  // Auto-prefetch first next page after initial data loads
  useEffect(() => {
    // Only prefetch if we have initial data and there might be more
    if (blogData.length >= 12) {
      prefetchNextPage(2);
    }
  }, [pageIndex]); // Only re-run when pageIndex changes

  return (
    <div className="w-full flex flex-col px-3 gap-5 mb-5">
      <div className="w-full flex flex-col gap-5">
        {/* Desktop Filters - Visible only on md and above */}
        <div className="w-full hidden md:flex justify-center items-center gap-5 ">
          <SelectArticleType
            categoryArray={blogCategory.filter((cat) => cat.text !== null)}
            placeHolder={t("blog.category")}
            setSelectedValue={(value) => {
              if (value.value === "clear") {
                setSearchParams({}, { replace: true });
              } else {
                setSearchParams({ category: value.value.toString().toLowerCase() }, { replace: true });
              }
            }}
            selectedValue={selectedCategoryId}
          />
          <SelectArticleType
            categoryArray={author.filter((aut) => aut.text !== null)}
            placeHolder={t("blog.author")}
            setSelectedValue={(value) => {
              if (value.value === "clear") {
                setSearchParams({}, { replace: true });
              } else {
                setSearchParams({ author: value.value.toString().toLowerCase() }, { replace: true });
              }
            }}
            selectedValue={selectedAuthorId}
          />
        </div>

        {/* Mobile Filter Sheet Trigger - Visible only on mobile */}
        <Sheet open={isSheetOpen} onOpenChange={(open) => setIsSheetOpen(open)}>
          <div
            className="flex flex-col items-end gap-2 fixed right-3 pr-3.5 z-10 md:hidden transition-opacity duration-300 ease-out"
            style={{
              bottom: showTopButton ? "7rem" : "4rem",
            }}
          >
            <SheetTrigger asChild>
              <button className="flex items-center justify-center size-10 rounded-full bg-textHeadingColor shadow-lg hover:shadow-xl transition-shadow">
                {hasFiltersApplied ? (
                  <FunnelX size={18} fill="white" className="text-white" />
                ) : (
                  <Funnel size={18} fill="white" className="text-white" />
                )}
              </button>
            </SheetTrigger>
          </div>
          <SheetContent
            side="bottom"
            className="!p-0 md:!pb-6 w-full max-md:h-[90vh] md:max-h-[calc(100vh-150px)] overflow-hidden flex flex-col !rounded-t-2xl"
            closeIcon={false}
          >
            <BlogFilterMobile
              categoryArray={blogCategory.filter((cat) => cat.text !== null)}
              authorArray={author.filter((aut) => aut.text !== null)}
              selectedCategoryId={selectedCategoryId}
              selectedAuthorId={selectedAuthorId}
              setIsSheetOpen={setIsSheetOpen}
            />
          </SheetContent>
        </Sheet>

        {blogData?.length > 0 ? (
          <ClientOnly
            fallback={
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-5 px-3 w-full">
                {blogData?.map((blogItem) => {
                  return (
                    <React.Fragment key={blogItem.id || blogItem.url_slug}>
                      <ArticleComponent article={blogItem} author={author} from={`${blogItem.type}/${blogItem.url_slug}`} />
                    </React.Fragment>
                  );
                })}
              </div>
            }
          >
            <InfiniteScroll
              dataLength={blogData.length}
              next={handleFetchNextPageBlogData}
              hasMore={hasMore}
              loader={
                <div className="w-full flex justify-center items-center">
                  <LoadingSpinner />
                </div>
              }
              endMessage={
                <div className="w-full flex justify-center items-center overflow-hidden">
                  <NoHasMoreText className="text-textSecondaryColor" text={t("common.seeAllData", { data: "blogs" })} />
                </div>
              }
              scrollThreshold={0.7}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-5 px-3 w-full">
                {blogData?.map((blog) => {
                  return (
                    <React.Fragment key={blog.id || blog.url_slug}>
                      <ArticleComponent article={blog} author={author} from={`${blog.type}/${blog.url_slug}`} />
                    </React.Fragment>
                  );
                })}
              </div>
            </InfiniteScroll>
          </ClientOnly>
        ) : (
          <div className="w-full flex justify-center items-center pt-28">
            <NodataFound text={`Jay Swaminarayan 🙏 No blogs found`} />
          </div>
        )}
      </div>
    </div>
  );
}

export const ErrorBoundary = () => {
  const error = useRouteError();
  return <NodataFound text="No data found for this categories" />;
};

export default Blog;
